import { useCallback, useEffect, useRef } from 'react';
import { useCanvasStore } from '../store/canvasStore';
import type { CanvasImage, ClipboardItem, SelectionType, TextItem } from '../types';

const clipboardData: ClipboardItem[] = [];

const useKeyboardEventHandler = (): void => {
  const stateRef = useRef<{
    selectedId: SelectionType;
    isEditingTextId: string | null;
    texts: TextItem[];
    canvasImages: CanvasImage[];
  }>({
    selectedId: null,
    isEditingTextId: null,
    texts: [],
    canvasImages: [],
  });

  useEffect(() => {
    stateRef.current = {
      selectedId: useCanvasStore.getState().selectedId,
      isEditingTextId: useCanvasStore.getState().isEditingTextId,
      texts: useCanvasStore.getState().texts,
      canvasImages: useCanvasStore.getState().canvasImages,
    };

    const unsubscribe = useCanvasStore.subscribe((state) => {
      stateRef.current = {
        selectedId: state.selectedId,
        isEditingTextId: state.isEditingTextId,
        texts: state.texts,
        canvasImages: state.canvasImages,
      };
    });

    const style = document.createElement('style');
    style.textContent = `
      .copy-flash {
        animation: copy-flash-animation 0.3s ease;
      }
      @keyframes copy-flash-animation {
        0% { opacity: 1; }
        50% { opacity: 0.5; }
        100% { opacity: 1; }
      }
    `;
    document.head.appendChild(style);

    return () => {
      unsubscribe();
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  const copyToClipboard = useCallback(() => {
    const { selectedId, texts, canvasImages } = stateRef.current;

    if (!selectedId || selectedId === 'canvas') {
      return;
    }

    clipboardData.length = 0;

    const selectedIds = Array.isArray(selectedId) ? selectedId : [selectedId];
    let copiedCount = 0;

    selectedIds.forEach((id) => {
      if (typeof id !== 'string') return;

      const textItem = texts.find((t) => t.id === id);
      if (textItem) {
        clipboardData.push({
          type: 'text',
          data: { ...textItem },
        });
        copiedCount++;
        return;
      }

      const imageItem = canvasImages.find((img) => img.id === id && !img.deleted);
      if (imageItem) {
        clipboardData.push({
          type: 'image',
          data: { ...imageItem },
        });
        copiedCount++;
      }
    });

    if (copiedCount > 0) {
      const selectedElements = document.querySelectorAll('.selected');
      selectedElements.forEach((el) => {
        el.classList.add('copy-flash');
        setTimeout(() => el.classList.remove('copy-flash'), 300);
      });
    }
  }, []);

  const cutSelected = useCallback(() => {
    const { selectedId, texts, canvasImages } = stateRef.current;
    if (!selectedId || selectedId === 'canvas') {
      return;
    }

    copyToClipboard();

    const ids = Array.isArray(selectedId) ? selectedId : [selectedId];

    ids.forEach((id) => {
      if (typeof id !== 'string') return;

      if (id === 'logo') {
        useCanvasStore.getState().setLogoImage(null);
        return;
      }

      if (texts.some((t) => t.id === id)) {
        useCanvasStore.getState().setTexts((prev) => prev.filter((t) => t.id !== id));
        return;
      }

      if (canvasImages.some((img) => img.id === id && !img.deleted)) {
        useCanvasStore.getState().updateImage(id, { deleted: true });
        return;
      }
    });

    useCanvasStore.getState().setSelectedId(null);
    useCanvasStore.getState().saveStateToHistory();
  }, [copyToClipboard]);

  const pasteFromClipboard = useCallback(async () => {
    if (!clipboardData.length) {
      return;
    }

    useCanvasStore.getState().saveStateToHistory();

    const PASTE_OFFSET = 20;
    const newSelectedIds: string[] = [];

    const canvasWidth = useCanvasStore.getState().stageRef.current?.width() || 800;
    const canvasHeight = useCanvasStore.getState().stageRef.current?.height() || 600;

    const stage = useCanvasStore.getState().stageRef.current;
    let mousePos = { x: 0, y: 0 };
    if (stage) {
      const stagePos = stage.getPointerPosition();
      if (stagePos) {
        mousePos = stagePos;
      }
    }

    let useMousePos = false;
    if (mousePos.x > 0 && mousePos.y > 0) {
      useMousePos = true;
    }

    const state = useCanvasStore.getState();
    const currentTexts = state.texts || [];
    const currentImages = state.canvasImages.filter((img) => !img.deleted) || [];

    const getMaxZIndex = () => {
      const textMax = currentTexts.length > 0 ? Math.max(...currentTexts.map((t) => t.zIndex || 0)) : 0;
      const imageMax = currentImages.length > 0 ? Math.max(...currentImages.map((i) => i.zIndex || 0)) : 0;
      return Math.max(textMax, imageMax, 0);
    };

    const nextZIndex = getMaxZIndex() + 1;

    clipboardData.forEach(async (item, index) => {
      if (item.type === 'text') {
        const textItem = item.data as TextItem;
        // Use crypto.randomUUID() for better randomness
        const newId = `text-${Date.now()}-${index}-${crypto.randomUUID().substring(0, 8)}`;

        let newX = textItem.x + PASTE_OFFSET;
        let newY = textItem.y + PASTE_OFFSET;

        if (useMousePos) {
          newX = mousePos.x;
          newY = mousePos.y;
          if (clipboardData.length > 1) {
            newX += index * 5;
            newY += index * 5;
          }
        }

        newX = Math.min(Math.max(0, newX), canvasWidth - textItem.width);
        newY = Math.min(Math.max(0, newY), canvasHeight - 50);

        const newText: TextItem = {
          ...textItem,
          id: newId,
          x: newX,
          y: newY,
          zIndex: nextZIndex + index,
        };

        useCanvasStore.getState().setTexts((prev) => [...prev, newText]);
        newSelectedIds.push(newId);
      } else if (item.type === 'image') {
        const imageItem = item.data as CanvasImage;

        if (imageItem.url) {
          const newId = await useCanvasStore.getState().addImage(imageItem.url);
          if (newId) {
            let newX = imageItem.x + PASTE_OFFSET;
            let newY = imageItem.y + PASTE_OFFSET;

            if (useMousePos) {
              newX = mousePos.x;
              newY = mousePos.y;
              if (clipboardData.length > 1) {
                newX += index * 5;
                newY += index * 5;
              }
            }

            newX = Math.min(Math.max(0, newX), canvasWidth - imageItem.width);
            newY = Math.min(Math.max(0, newY), canvasHeight - imageItem.height);

            useCanvasStore.getState().updateImage(newId, {
              x: newX,
              y: newY,
              width: imageItem.width,
              height: imageItem.height,
              opacity: imageItem.opacity,
              rotation: imageItem.rotation,
              animation: imageItem.animation,
              animationDelay: imageItem.animationDelay,
              animationDuration: imageItem.animationDuration,
              zIndex: nextZIndex + index,
            });

            newSelectedIds.push(newId);
          }
        }
      }
    });

    // This needs to be moved inside a Promise.all to ensure it runs after all images are added
    setTimeout(async () => {
      await Promise.all(newSelectedIds.map((id) => Promise.resolve())); // Wait for all operations to complete
      useCanvasStore.getState().updateElementOrder();

      if (newSelectedIds.length === 1) {
        useCanvasStore.getState().setSelectedId(newSelectedIds[0]);
      } else if (newSelectedIds.length > 1) {
        useCanvasStore.getState().setSelectedId(newSelectedIds);
      }
    }, 100);
  }, []);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const activeElement = document.activeElement;
      const isInInput =
        activeElement instanceof HTMLInputElement ||
        activeElement instanceof HTMLTextAreaElement ||
        activeElement instanceof HTMLSelectElement;

      if (stateRef.current.isEditingTextId || isInInput) {
        return;
      }

      const isModifierKey = e.metaKey || e.ctrlKey;

      if (isModifierKey && (e.key === 'c' || e.key === 'C')) {
        if (stateRef.current.selectedId && stateRef.current.selectedId !== 'canvas') {
          e.preventDefault();
          e.stopPropagation();
          copyToClipboard();

          const stage = useCanvasStore.getState().stageRef.current;
          if (stage) {
            const selectedIds = Array.isArray(stateRef.current.selectedId)
              ? stateRef.current.selectedId
              : [stateRef.current.selectedId];

            selectedIds.forEach((id) => {
              const konvaNode = stage.findOne(`#${id}`) || stage.findOne(`#text-${id}`);
              if (konvaNode) {
                const originalOpacity = konvaNode.opacity();
                konvaNode.opacity(0.5);
                setTimeout(() => {
                  konvaNode.opacity(originalOpacity);
                  konvaNode.getLayer()?.batchDraw();
                }, 200);
                konvaNode.getLayer()?.batchDraw();
              }
            });
          }
        }
        return;
      }

      if (isModifierKey && (e.key === 'x' || e.key === 'X')) {
        if (stateRef.current.selectedId && stateRef.current.selectedId !== 'canvas') {
          e.preventDefault();
          e.stopPropagation();
          cutSelected();
        }
        return;
      }

      if (isModifierKey && (e.key === 'v' || e.key === 'V')) {
        e.preventDefault();
        e.stopPropagation();
        pasteFromClipboard();
        return;
      }

      if ((e.key === 'Delete' || e.key === 'Backspace') && stateRef.current.selectedId) {
        if (stateRef.current.selectedId !== 'canvas') {
          e.preventDefault();
          e.stopPropagation();
          useCanvasStore.getState().deleteSelected();
        }
        return;
      }

      if (isModifierKey && !e.shiftKey && (e.key === 'z' || e.key === 'Z')) {
        e.preventDefault();
        e.stopPropagation();
        useCanvasStore.getState().undoChange();
        return;
      }

      if (
        (isModifierKey && e.shiftKey && (e.key === 'z' || e.key === 'Z')) ||
        (isModifierKey && (e.key === 'y' || e.key === 'Y'))
      ) {
        e.preventDefault();
        e.stopPropagation();
        useCanvasStore.getState().redoChange();
        return;
      }

      if (isModifierKey && (e.key === 'a' || e.key === 'A')) {
        e.preventDefault();
        e.stopPropagation();

        const { texts, canvasImages } = stateRef.current;
        const allIds = [...texts.map((t) => t.id), ...canvasImages.filter((img) => !img.deleted).map((img) => img.id)];

        if (allIds.length > 0) {
          useCanvasStore.getState().setSelectedId(allIds.length === 1 ? allIds[0] : allIds);
        }
      }
    },
    [copyToClipboard, cutSelected, pasteFromClipboard]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown, { capture: true });

    return () => {
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [handleKeyDown]);
};

export default useKeyboardEventHandler;
